/**
 * Поля ввода с правилами предметной области.
 *
 * Вынесены отдельно потому, что ИНН и СНИЛС встречаются в четырёх местах
 * (должник, работодатель, кредитор, организация), и правило проверки
 * должно быть одно (BLUEPRINT, apps/web/components/fields).
 */

import { useEffect, useId, useRef, useState } from 'react';
import { formatSnils, innError, isValidInn, snilsError } from '@bd/domain';
import { api } from '../../api.js';

// ---------------------------------------------------------------- обёртка

export function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label: string;
  error?: string | null;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`field${error ? ' field-error' : ''}`}>
      <span className="field-label">
        {label}
        {required && <span className="required" title="Обязательное поле"> *</span>}
      </span>
      {children}
      {/* Ошибка показывается рядом с полем и одновременно попадает в панель готовности (ТЗ п. 8.4) */}
      {error && <span className="field-message">{error}</span>}
      {!error && hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function TextField({
  label,
  value,
  onChange,
  required,
  error,
  hint,
  placeholder,
  readOnly,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string | null;
  hint?: string;
  placeholder?: string;
  readOnly?: boolean;
  type?: string;
}) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
  readOnly,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="field field-inline">
      <input type="checkbox" checked={checked} disabled={readOnly} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  required,
  error,
  /** Все перечисления допускают ввод собственного значения (ТЗ FR-14) */
  allowCustom = true,
  readOnly,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
  error?: string | null;
  allowCustom?: boolean;
  readOnly?: boolean;
}) {
  const listId = useId();
  if (!allowCustom) {
    return (
      <Field label={label} error={error} required={required}>
        <select value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
          <option value="" />
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  return (
    <Field label={label} error={error} required={required} hint="Можно ввести своё значение">
      <input list={listId} value={value} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </Field>
  );
}

// -------------------------------------------------------------------- ИНН

export function InnField({
  label = 'ИНН',
  value,
  onChange,
  kind,
  required,
  /** Подстановка реквизитов по ИНН через DaData (FR-13) */
  onResolved,
  readOnly,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  kind?: 'person' | 'organization';
  required?: boolean;
  onResolved?: (party: { name: string; inn: string; address: string }) => void;
  readOnly?: boolean;
}) {
  const [touched, setTouched] = useState(false);
  const [lookup, setLookup] = useState<{ busy: boolean; message: string | null }>({ busy: false, message: null });

  const error = touched && value ? innError(value, kind) : null;

  const resolve = async () => {
    if (!onResolved || !isValidInn(value, kind)) return;
    setLookup({ busy: true, message: null });
    try {
      const { suggestions } = await api.dadata.findByInn(value);
      const first = suggestions[0];
      if (first) {
        onResolved(first.entry);
        setLookup({ busy: false, message: first.warning });
      } else {
        setLookup({ busy: false, message: 'Организация не найдена' });
      }
    } catch (e) {
      // Недоступность подсказок не мешает работе: реквизиты вводятся вручную
      setLookup({ busy: false, message: (e as Error).message });
    }
  };

  return (
    <Field label={label} error={error} required={required} hint={lookup.message ?? undefined}>
      <div className="field-row">
        <input
          value={value ?? ''}
          inputMode="numeric"
          maxLength={12}
          readOnly={readOnly}
          onBlur={() => setTouched(true)}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 12))}
        />
        {onResolved && (
          <button
            type="button"
            className="link-button"
            disabled={readOnly || lookup.busy || !isValidInn(value, kind)}
            onClick={resolve}
            title="Подставить реквизиты по ИНН"
          >
            {lookup.busy ? '…' : 'Найти'}
          </button>
        )}
      </div>
    </Field>
  );
}

// ------------------------------------------------------------------ СНИЛС

export function SnilsField({
  value,
  onChange,
  required,
  readOnly,
}: {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  readOnly?: boolean;
}) {
  const [touched, setTouched] = useState(false);
  const error = touched && value ? snilsError(value) : null;

  return (
    <Field label="СНИЛС" error={error} required={required} hint="Формат 999-999-999 99">
      <input
        value={value ?? ''}
        inputMode="numeric"
        readOnly={readOnly}
        onBlur={() => {
          setTouched(true);
          // Форматирование на уходе фокуса, а не на каждом нажатии:
          // маска, переставляющая курсор, мешает быстрому вводу.
          onChange(formatSnils(value));
        }}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

// ---------------------------------------------------------------- деньги

/** Денежное поле. В профиле сумма хранится в копейках целым числом. */
export function MoneyField({
  label,
  kopecks,
  onChange,
  required,
  readOnly,
  error,
}: {
  label: string;
  kopecks: number;
  onChange: (kopecks: number) => void;
  required?: boolean;
  readOnly?: boolean;
  error?: string | null;
}) {
  const [text, setText] = useState(() => kopecksToText(kopecks));
  const lastValue = useRef(kopecks);

  // Внешнее изменение (пересчёт итога, загрузка профиля) отражается в поле,
  // но не мешает вводу: пока пользователь печатает, значение не перетирается.
  useEffect(() => {
    if (kopecks !== lastValue.current) {
      lastValue.current = kopecks;
      setText(kopecksToText(kopecks));
    }
  }, [kopecks]);

  return (
    <Field label={label} required={required} error={error}>
      <input
        value={text}
        inputMode="decimal"
        readOnly={readOnly}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^\d,.\s]/g, '');
          setText(raw);
          const parsed = textToKopecks(raw);
          lastValue.current = parsed;
          onChange(parsed);
        }}
        onBlur={() => setText(kopecksToText(textToKopecks(text)))}
      />
    </Field>
  );
}

export function kopecksToText(kopecks: number): string {
  if (!kopecks) return '';
  const rubles = Math.trunc(kopecks / 100);
  const rest = Math.abs(kopecks % 100);
  return `${rubles.toLocaleString('ru-RU')},${String(rest).padStart(2, '0')}`;
}

export function textToKopecks(text: string): number {
  const normalized = (text ?? '').replace(/\s/g, '').replace(',', '.');
  if (!normalized) return 0;
  const value = Number(normalized);
  return Number.isFinite(value) ? Math.round(value * 100) : 0;
}

// ------------------------------------------------------------------ даты

export function DateField({
  label,
  value,
  onChange,
  required,
  error,
  readOnly,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  required?: boolean;
  error?: string | null;
  readOnly?: boolean;
}) {
  return (
    <Field label={label} required={required} error={error}>
      <input type="date" value={value ?? ''} readOnly={readOnly} onChange={(e) => onChange(e.target.value || null)} />
    </Field>
  );
}

// ----------------------------------------------------------------- адрес

// Составной адрес вынесен в отдельный файл: он вырос до самостоятельного
// блока со списком субъектов и сборкой строки из полей
export { AddressField, type AddressFieldProps, type RegionOption } from './AddressField.js';
